import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Checkbox, Modal, Switch, TextArea, useToast } from "@aidkitorg/component-library";
import * as v0 from '@aidkitorg/types/lib/survey';
import { collectScopeForTags } from '@aidkitorg/types/lib/translation/permissions';
import { expandTemplates, extractNotificationsAndPayments, hash } from '@aidkitorg/types/lib/translation/v0_to_legacy';
import { Author, ChangeSet, generateUserLocation, getCompressedEvents, mergeStateFromEvents, Patch, reconstructStateFromEvents, State, TrackedObject, updateUserActivity, UserActivity } from '@aidkitorg/typesheets/lib/collab';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal as BootstrapModal } from "react-bootstrap";
import { get_deployment, usePost, useRoboScreener } from "./API";
import { QuestionTypes } from "./Applicant";
import { useCollabActions } from './CollabWrapper';
import { ExcludableType, EXCLUDABLE_TYPES_ARRAY, QuestionNodeModal } from "./CondensedSurveyView";
import InterfaceContext, { LoggedInConfigurationContext, SupportedLanguage, ConfigurationContext } from './Context';
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { langToWord, supportedLanguages, useLocalizedStrings } from './Localization';
import { FacePile } from "./Realtime";
import { generateRandomString, languageContent, nonHookMarkdown, removeDuplicateEvents, SpacedSpinner, stringToColor } from './Util';
import { conditionalContentToPathNodes, ConditionalNode, mapSubsurveyPaths, PathNode, QuestionNodeType, SectionNode, SubsurveyNode, TemplatedBlockNodeType } from "./utils/mapSubsurveyPaths";
import { QuestionProps } from "./Questions/Props";
import { TRANSLATORS } from "@aidkitorg/types/lib/translation/questions";
import { AirtableSurvey, AirtableSurveyQuestion } from "@aidkitorg/types/lib/legacy/airtable";
import { CompileExpressionToEnglish } from "@aidkitorg/types/lib/translation/expr_to_english";
import { useHistory, useLocation } from "react-router-dom";
import { RoboscreenerSurveyErrorsModal } from "./RoboscreenerSurveyErrorsModal";
import { validateDistro } from "@aidkitorg/types/lib/distroValidator/distroValidator";

type CopyEdits = { edits: Record<string, Partial<Record<SupportedLanguage, { value: string, prev_gen: number }>>>, latestEditedObjectId?: string }

const QuestionNode = React.memo(function QuestionNode(props: {
  questionNode: QuestionNodeType,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  displayIndex: string,
  addChange: React.MutableRefObject<((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void) | undefined>
  isSubQuestion?: boolean,
  canEdit?: boolean,
  renderMarkdown?: boolean
}) {

  const sendChangesDebounceTimer = useRef<NodeJS.Timeout>();

  // Same function used for editor and preview to determine if disabled.
  // If no tracked object, then we can't edit.
  function getElementDisabledAndEmptyText(lang: SupportedLanguage): [boolean, string] {
    const noObject = props.questionNode.trackedObjInfo?.[lang] === undefined;
    let potentialConfigurationIssueWarning = '';
    if (lang !== 'en' && noObject && props.questionNode.trackedObjInfo?.['en'] !== undefined) {
      potentialConfigurationIssueWarning = '⚠️ ';
    }
    return [noObject, `[[${potentialConfigurationIssueWarning}Not configured for this language]]`];
  }

  const [previewDisabledLeft, previewNotConfiguredTextLeft] = getElementDisabledAndEmptyText(props.previewLang);
  const [previewDisabledCenter, previewNotConfiguredTextCenter] = getElementDisabledAndEmptyText(props.editorLang);
  const [editorDisabledNoObject, editorNotConfiguredText] = getElementDisabledAndEmptyText(props.editorLang);
  const editorDisabled = !props.canEdit || editorDisabledNoObject;

  const [isModalOpen, setIsModalOpen] = useState(false);
  // This is to prevent lag between local edits and updates to overall collab state.
  // TODO: seeing if this will alleviate cursor jumping: https://react.dev/reference/react-dom/components/input#my-input-caret-jumps-to-the-beginning-on-every-keystroke
  // See also: https://react.dev/reference/react/useCallback (Should you add useCallback everywhere? halfway down the page)
  // See also: https://giacomocerquone.com/blog/keep-input-cursor-still/
  const [localEditorText, setLocalEditorText] =
    useState(
      editorDisabledNoObject
        ? editorNotConfiguredText
        : props.questionNode.content?.[props.editorLang] ?? ''
    )

  const otherEditors = (props.questionNode.trackedObjInfo?.[props.editorLang]?.userActivity || [])
    .filter(editor => (editor.uid !== props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor?.uid || editor.tabId !== props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor?.tabId))
    .map((userActivity) => {
      return {
        ...userActivity,
        color: stringToColor(userActivity.name + '/' + userActivity.tabId)
      }
    });

  const editingAuthorsNotMe = useMemo<boolean>(() => {
    const viewingAuthor = props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor;
    return (props.questionNode.trackedObjInfo?.[props.editorLang]?.userActivity || [])
      .some((editingAuthor) => (editingAuthor.uid !== viewingAuthor?.uid || editingAuthor.tabId !== viewingAuthor?.tabId));
  }, [props.questionNode.trackedObjInfo, props.editorLang])

  // localEditorText is intentionally omitted from dependencies, because we don't want this
  // being called for edits we make ourselves. React advises against this, so it could be worth
  // investigating if there's a more canonical solution to this.
  useEffect(() => {
    // this can cause cursor jumping issues if we are the one editing, and then our own changes get propogated through.
    // we only really care for this useEffect when someone else is also editing the text field.
    if (editingAuthorsNotMe && localEditorText !== props.questionNode.content?.[props.editorLang]) {
      setLocalEditorText(props.questionNode.content?.[props.editorLang] ?? '');
    }
  }, [editingAuthorsNotMe, props.questionNode.content, props.editorLang]);

  const onChangeHandler = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (!editorDisabled && props.addChange.current) {
      setLocalEditorText(e.target.value);
      // Okay to assert trackedObjInfo not undefined because if it were, editorDisabled would be true
      // and this would be unreachable.
      if (sendChangesDebounceTimer.current) {
        clearTimeout(sendChangesDebounceTimer.current);
      }
      sendChangesDebounceTimer.current = setTimeout(() => {
        props.addChange.current!(e.target.value, props.editorLang, props.questionNode.trackedObjInfo!);
      }, 500);
    }
  }, [editorDisabled, props.editorLang, props.questionNode.trackedObjInfo]);

  const previewTextLeft = previewDisabledLeft
    ? previewNotConfiguredTextLeft
    : (props.previewLang === props.editorLang
      ? localEditorText
      : props.questionNode.content?.[props.previewLang] ?? '');

  const previewTextCenter = previewDisabledCenter
    ? previewNotConfiguredTextCenter
    : (props.previewLang === props.editorLang
      ? localEditorText
      : props.questionNode.content?.[props.editorLang] ?? '');

  // What if instead we did the following:
  // - Question info (kind, index, optional, hidden, target field, etc.) is placed in a small divider between each question
  // - An additional "preview" column is added left of the central preview column
  // - The markdown toggle works on both preview columns
  // - The central preview column and the editor column use the same language ("editor")

  return (
    <div>
      <div className='w-1/5 cursor-pointer text-black hover:underline' onClick={() => setIsModalOpen(true)}>
        <span style={{ background: stringToColor(props.questionNode.questionType), borderColor: stringToColor(props.questionNode.questionType, '85%') }} className='mr-1 rounded-xl px-1 py-[1px] border-[1px] text-xs'>{props.questionNode.questionType}</span>
        <span>{props.displayIndex}</span>
        {props.questionNode.optional && <span className='bg-amber-100 mx-1 rounded-xl px-1 py-[1px] border-[1px] border-amber-200 text-xs'>Optional</span>}
        {props.questionNode.hidden && <span className='bg-amber-100 mx-1 rounded-xl px-1 py-[1px] border-[1px] border-amber-200 text-xs'>Hidden</span>}
        {props.questionNode.targetField && <span> - {props.questionNode.targetField}</span>}
      </div>
      <div className='flex justify-between pb-4 pr-4'>
        {(props.questionNode.content?.[props.previewLang] || localEditorText) && (
          <>
            {/* Preview - left column */}
            <div className={`w-[526px] ${props.isSubQuestion ? 'ml-16 mr-1 my-1' : 'm-1'}`}>
              {props.renderMarkdown
                ? (
                  <div className={`font-sans text-sm leading-6 block whitespace-pre-wrap rounded-md ${previewDisabledLeft ? 'bg-gray-100' : 'bg-white'} border p-2`}>
                    {nonHookMarkdown(previewTextLeft)}
                  </div>
                ) : (
                  <pre className={`font-sans text-sm leading-6 block whitespace-pre-wrap rounded-md ${previewDisabledLeft ? 'bg-gray-100' : 'bg-white'} border p-2`}>
                    {previewTextLeft}
                  </pre>
                )
              }
            </div>
            {/* Preview - central column */}
            <div className={`w-[526px] ${props.isSubQuestion ? 'ml-16 mr-1 my-1' : 'm-1'}`}>
              {props.renderMarkdown
                ? (
                  <div className={`font-sans text-sm leading-6 block whitespace-pre-wrap rounded-md ${previewDisabledLeft ? 'bg-gray-100' : 'bg-white'} border p-2`}>
                    {nonHookMarkdown(previewTextCenter)}
                  </div>
                ) : (
                  <pre className={`font-sans text-sm leading-6 block whitespace-pre-wrap rounded-md ${previewDisabledLeft ? 'bg-gray-100' : 'bg-white'} border p-2`}>
                    {previewTextCenter}
                  </pre>
                )
              }
            </div>
            {/* Editor - right column */}
            <div className='w-[526px]'>
              {/* DEBUG */}
              {/* <div>{props.questionNode.trackedObjInfo?.[props.editorLang]?.id}</div> */}

              {/* The color box that surrounds an editor when another user is working there */}
              <div className='flex flex-row'>
                {otherEditors.map(ec => {
                  return <div key={ec.uid + ec.tabId}
                    style={{
                      position: 'relative',
                      backgroundColor: ec.color,
                      paddingLeft: '2px',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '500',
                      border: `3px solid ${ec.color}`,
                      borderTopLeftRadius: '10px',
                      borderTopRightRadius: '10px',
                      height: '20px',
                      width: `${100 / otherEditors.length}%`
                    }}>
                    {ec.name}
                  </div>
                })}
              </div>
              {/* The editor component */}
              <div className='h-full mt-1' style={(otherEditors.length ? { border: `3px solid ${otherEditors[0].color}`, borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' } : {})}>
                <TextArea
                  label={`Editable text area for question ${props.questionNode.targetField + props.displayIndex}`}
                  disabled={editorDisabled}
                  className='rounded-md disabled:bg-gray-200 h-full'
                  rows={1}
                  value={localEditorText}
                  onChange={onChangeHandler}
                />
              </div>
            </div>
          </>
        )}
        <QuestionNodeModal
          questionType={props.questionNode.questionType}
          displayIndex={props.displayIndex}
          targetField={props.questionNode.targetField}
          choices={props.questionNode.choices}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          content={props.questionNode.content?.[props.previewLang]}
        />
      </div>
    </div>
  );
});

function TemplatedBlockNode(props: {
  templatedBlockNode: TemplatedBlockNodeType,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  excludedTypes: Record<ExcludableType, boolean>,
  addChange: React.MutableRefObject<((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void) | undefined>,
  isPreviewMode: boolean,
  displayIndex: string,
  indexForKey: number,
  canEdit?: boolean,
  renderMarkdown?: boolean
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className='bg-yellow-100 border-t-2 border-l-2 border-slate-300 border-dashed rounded-t-lg'>
      <div className='pl-1 cursor-pointer text-black font-medium hover:underline' onClick={() => setIsModalOpen(true)}>Templated Block</div>
      <BootstrapModal size="lg" show={isModalOpen} onHide={() => setIsModalOpen(false)} className='relative'>
        <BootstrapModal.Header closeButton={true}>
          <BootstrapModal.Title>Templated Block Substitutions</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {props.templatedBlockNode.iterations.map((iteration, i) => {
            return (
              <Accordion type='multiple' key={'iteration' + i + '.'}>
                <AccordionItem value={'item' + i}>
                  <AccordionTrigger className='text-base'>{'Substitution ' + i}</AccordionTrigger>
                  <AccordionContent>
                    {iteration.substitutions.map((substitution, j) =>
                      <div key={'substitution ' + i + '.' + j}>
                        <span>{substitution.key} : </span>
                        <span>{substitution.value}</span>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            );
          })}
        </BootstrapModal.Body>
      </BootstrapModal>
      <div className='pl-4 relative bg-yellow-50'>
        <Node
          nodes={(props.templatedBlockNode.subNodes || [])}
          previewLang={props.previewLang}
          editorLang={props.editorLang}
          excludedTypes={props.excludedTypes}
          addChange={props.addChange}
          prevIndex={props.displayIndex}
          indexForKey={props.indexForKey}
          isPreviewMode={props.isPreviewMode}
          canEdit={props.canEdit}
          renderMarkdown={props.renderMarkdown}
        />
      </div>
    </div>
  );
}

// TODO(Riley): PreviewMode should likely be supported for non-collab programs... figure that out
/**
 * This component is used to display each node/question in a survey in a manner very similar to what
 * a participant would see when filling out a survey with a few additional features such as:
 *  - All Likerts and Explanations starting out fully expanded
 *  - All paths through conditional branches are shown, and their condition logic is displayed
 *  - All questions are visible simultaneously without needing to be "answered"
 *
 * The original motivation for this view is to provide a means to create a PDF of an entire survey
 * in a way that is human-readable and requires minimal manual work
 */
function PreviewNode(props: {
  subNode: QuestionNodeType,
  previewLang: SupportedLanguage,
  excludedTypes: Record<ExcludableType, boolean>,
}) {
  const translatedQuestions: AirtableSurveyQuestion[] = [];
  if (props.subNode.kind) {
    // Special handling for preview of different components

    // Expand all Explanations
    if (props.subNode.kind === 'Explanation') {
      props.subNode.node.collapsible = false;
    }
    // This ensures that all likert subquestions are visible,
    // without having to actually answer them
    if (props.subNode.kind === 'Likert') {
      props.subNode.node.showAll = true;
    }

    let Translations: AirtableSurvey['Translations'] = [];
    // The code that makes this work in normal flows is in questions.ts and v0_to_legacy.ts
    TRANSLATORS[props.subNode.kind]?.addFunc(props.subNode.node,
      {
        Question: (q) => { translatedQuestions.push(q) },
        // Not needed because the "modern type" doesn't use this. Instead, the whole
        // component is passed into Question() and it handles the various languages
        Translation: (q) => '',
        Choice: (q) => {
          // This is a hack, but we need the value to be an object like { en: string } not string like the function wants
          (q.label as any)['Name'] = q.label?.[props.previewLang];
          return q.label as unknown as string
        },
        Role: (q) => '',
        Section: (q) => '',
        Depth: 0,
      });
  }

  return <>
    {!props.excludedTypes[props.subNode.questionType as ExcludableType] && (
      <div className='flex justify-between pb-4'>
        <div className={'rounded-md w-[600px] border p-2 m-1 bg-white'}>
          {/* Small hack: render a simple markdown div for ConditionalContent text, otherwise, render the real component UI */}
          {props.subNode.questionType === 'ConditionalContent'
            ? <div>{nonHookMarkdown(props.subNode.content?.[props.previewLang])}</div>
            : translatedQuestions.map((question, i) => {
              // Likerts are strange and have multiple subquestions that we don't want to render here.
              // Other question types like Liveness also have subquestions and those we *do* want to render.
              if (translatedQuestions[0].fields["Field Type"] === 'Likert' && i > 0) return <></>;

              // Some question kinds like "Milestone" don't have a corresponding QuestionType, so skip them.
              return (question?.fields?.["Field Type"] && question.fields["Field Type"] in QuestionTypes) && (
                // This should end up looking very close to what a particpant would see in a live survey
                React.createElement(
                  QuestionTypes[question.fields["Field Type"]],
                  {
                    key: question.id,
                    "Field Type": question.fields["Field Type"],
                    Viewer: 'applicant',
                    Question: question.id,
                    "English Content": '', // likely overwritten by [languageContent...] below, but needed for TS "satisfies"
                    ...question.fields,
                    [languageContent(props.previewLang)]: props.subNode?.content?.[props.previewLang],
                    setInfoKey: (key: string, value: any, valid: boolean, disqualifies: boolean) => { },
                    // TODO(Riley): we have two options here:
                    // - we could supply a test UID and load their data to be passed into this which would be
                    //   most realistic but would cause certain components to render in unhelpful ways (think encrypted text)
                    // - we could create a single dummy dictionary of components for which having some data is
                    //   necessary in order to render them realistically (think LikertDataSource)
                    //
                    // This does seem to work for some components (TextEntry), but not others (Select)...
                    info: {},
                  // "satisfies" not used here because 'Options (if relevant)' property of AirtableSurveyQuestion
                  // returned by TRANSLATORS doesn't match expected type for same property in type QuestionProps
                  } as QuestionProps
                )
              );
            }
            )}
        </div>
      </div>
    )}
  </>
}

/**
 * A node representing a condition and its branches.
 * props.subNodesTrue should always be included and represents the path where the condition is met,
 * props.subNodesFalse is optional and represents the path where the condition is not met.
 */
function ConditionNode(
  props: {
    previewLang: SupportedLanguage,
    editorLang: SupportedLanguage,
    excludedTypes: Record<ExcludableType, boolean>,
    addChange: React.MutableRefObject<((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void) | undefined>,
    isPreviewMode: boolean,
    indexForKey: number,
    conditionProps: { condition: string, conditionDisplayIndex: string, subNodesTrue: PathNode[], subNodesFalse?: PathNode[] },
    canEdit?: boolean,
    renderMarkdown?: boolean
  }) {
  return (
    <div className={`bg-blue-100 border-t-2 border-l-2 border-slate-300 border-dashed rounded-t-lg ${props.isPreviewMode ? 'w-[660px]' : ''}`}>
      <div className='pl-1 flex justify-between'>{props.conditionProps.condition}</div>
      <div className='bg-teal-50 pl-4 relative'>
        <Node
          previewLang={props.previewLang}
          editorLang={props.editorLang}
          excludedTypes={props.excludedTypes}
          addChange={props.addChange}
          nodes={(props.conditionProps.subNodesTrue || [])}
          prevIndex={props.conditionProps.conditionDisplayIndex}
          indexForKey={props.indexForKey}
          conditionForKey={props.conditionProps.condition}
          isPreviewMode={props.isPreviewMode}
          canEdit={props.canEdit}
          renderMarkdown={props.renderMarkdown}
        />
      </div>
      {props.conditionProps.subNodesFalse !== undefined && (
        <div className='bg-purple-100 pl-4 relative'>
          <Node
            previewLang={props.previewLang}
            editorLang={props.editorLang}
            excludedTypes={props.excludedTypes}
            addChange={props.addChange}
            nodes={(props.conditionProps.subNodesFalse || [])}
            prevIndex={props.conditionProps.conditionDisplayIndex}
            indexForKey={props.indexForKey}
            conditionForKey={props.conditionProps.condition}
            isPreviewMode={props.isPreviewMode}
            canEdit={props.canEdit}
            renderMarkdown={props.renderMarkdown}
          />
        </div>
      )}
    </div>
  )
}

// TODO(Riley) - Can we combine these props somehow or otherwise avoid passing them all directly everywhere?
function SubNode(props: {
  subNode: PathNode,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  excludedTypes: Record<ExcludableType, boolean>,
  addChange: React.MutableRefObject<((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void) | undefined>,
  isPreviewMode: boolean,
  prevIndex?: string,
  indexForKey: number,
  conditionForKey?: string,
  outerCount: number,
  canEdit?: boolean,
  renderMarkdown?: boolean
}) {
  let mutableCount = props.outerCount;
  const displayIndex = (props.prevIndex ? props.prevIndex + '.' + mutableCount : mutableCount) + ''; // cast to string

  switch (props.subNode.type) {
    case 'Question':
      mutableCount++
      if (props.isPreviewMode) {
        return <>
          {
            /*
            * Below is needed to preview conditional content correctly.
            * First we look for whether there are any Conditions in the subContent of this node,
            * and if we find any, we split out the subcontent into PreviewNodes and ConditionNodes.
            * TODO: could we use the ConditionalContent question type here instead to be more explicit?
            */
          }
          {((props.subNode.subContent || []).some((subNode) => subNode.type === 'Condition'))
            ? props.subNode.subContent?.map((subQuestion, subQIndex) => {
              if (subQuestion.type === 'Question') {
                return <PreviewNode subNode={subQuestion} previewLang={props.previewLang} excludedTypes={props.excludedTypes} />
              } else if (subQuestion.type === 'Condition') {
                return <ConditionNode
                  key={subQuestion.condition + displayIndex + props.indexForKey + props.conditionForKey}
                  previewLang={props.previewLang}
                  editorLang={props.editorLang}
                  excludedTypes={props.excludedTypes}
                  addChange={props.addChange}
                  isPreviewMode={props.isPreviewMode}
                  indexForKey={props.indexForKey}
                  conditionProps={{
                    condition: subQuestion.condition,
                    conditionDisplayIndex: displayIndex + '→' + subQIndex,
                    subNodesTrue: (subQuestion.subNodesTrue || []),
                    subNodesFalse: (subQuestion.subNodesFalse || [])
                  }}
                  canEdit={props.canEdit}
                  renderMarkdown={props.renderMarkdown}
                />
              }
            })
            : <PreviewNode subNode={props.subNode} previewLang={props.previewLang} excludedTypes={props.excludedTypes} />
          }
        </>
      }
      return (
        <>
          {!props.excludedTypes[props.subNode.questionType as ExcludableType] && (
            <>
              {/* We include editorLang and previewLang in the key to make React reset the component when those change */}
              {/* DEBUG: <div className='bg-red-300'>{props.subNode.trackedObjInfo?.en?.prev_gen}</div> */}
              <QuestionNode
                key={(props.subNode.targetField || props.subNode.questionType) + displayIndex + props.indexForKey + props.conditionForKey + props.editorLang + props.previewLang}
                questionNode={props.subNode}
                previewLang={props.previewLang}
                editorLang={props.editorLang}
                displayIndex={displayIndex}
                addChange={props.addChange}
                canEdit={props.canEdit}
                renderMarkdown={props.renderMarkdown}
              />
              {(props.subNode.subContent ?? []).map((subQuestion, subQIndex) => {
                return <SubNode
                  key={props.subNode.type + displayIndex + props.indexForKey + props.conditionForKey + subQIndex + props.editorLang + props.previewLang}
                  subNode={subQuestion}
                  previewLang={props.previewLang}
                  editorLang={props.editorLang}
                  excludedTypes={props.excludedTypes}
                  addChange={props.addChange}
                  isPreviewMode={props.isPreviewMode}
                  indexForKey={subQIndex}
                  prevIndex={displayIndex + '→' + subQIndex}
                  outerCount={1}
                  canEdit={props.canEdit}
                  renderMarkdown={props.renderMarkdown}
                />
              })}
            </>
          )}
        </>
      )
    case 'Section':
      return <h4 key={props.subNode.name + displayIndex + props.indexForKey + props.conditionForKey} className='mt-2'>
        {props.subNode.name[props.previewLang]}
      </h4>;
    case 'Condition':
      // It is possible to have multiple conditions with the same displayIndex and condition, hence the other pieces of the key
      return <ConditionNode
        key={hash(props.subNode.condition) + displayIndex + props.indexForKey + props.conditionForKey}
        previewLang={props.previewLang}
        editorLang={props.editorLang}
        excludedTypes={props.excludedTypes}
        addChange={props.addChange}
        isPreviewMode={props.isPreviewMode}
        indexForKey={props.indexForKey}
        conditionProps={{
          condition: props.subNode.condition,
          conditionDisplayIndex: displayIndex,
          subNodesTrue: (props.subNode.subNodesTrue || []),
          subNodesFalse: (props.subNode.subNodesFalse || [])
        }}
        canEdit={props.canEdit}
        renderMarkdown={props.renderMarkdown}
      />
    case 'Templated Block':
      // TODO: maybe we should hash the iterations part of the templated block to assign it a unique ID/key?
      return <TemplatedBlockNode
        key={props.subNode.iterations.length + displayIndex + props.indexForKey + props.conditionForKey}
        displayIndex={displayIndex}
        indexForKey={props.indexForKey}
        excludedTypes={props.excludedTypes}
        templatedBlockNode={props.subNode}
        previewLang={props.previewLang}
        editorLang={props.editorLang}
        addChange={props.addChange}
        isPreviewMode={props.isPreviewMode}
        canEdit={props.canEdit}
        renderMarkdown={props.renderMarkdown}
      />
    default:
      return <div key={displayIndex + props.indexForKey}>Unknown node type: {props.subNode.type}</div>;
  }
}

const Node = React.memo(function SubNodeOuter(props: {
  nodes: PathNode[] | undefined,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  excludedTypes: Record<ExcludableType, boolean>,
  addChange: React.MutableRefObject<((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void) | undefined>,
  isPreviewMode: boolean,
  prevIndex?: string,
  indexForKey?: number,
  conditionForKey?: string,
  canEdit?: boolean,
  renderMarkdown?: boolean
}) {

  return <>
    {(props.nodes || []).map((subNode, i) =>
      <SubNode
        // TODO: Can we instead use a hash of like... kind + content + ? instead of indexes
        // Alternatively, can we just use TrackedObjectInfo
        // This could be important in cases where someone in Distro adds/removes/moves stuff and the
        // indexes change
        key={(i + 1) + '.' + (props.indexForKey ?? 1)}
        subNode={subNode}
        previewLang={props.previewLang}
        editorLang={props.editorLang}
        excludedTypes={props.excludedTypes}
        addChange={props.addChange}
        prevIndex={props.prevIndex}
        indexForKey={i + 1}
        conditionForKey={hash(props.conditionForKey)}
        outerCount={i + 1}
        isPreviewMode={props.isPreviewMode}
        canEdit={props.canEdit}
        renderMarkdown={props.renderMarkdown}
      />
    )}
  </>
});

const URL_MODE_KEY = 'mode';
const URL_MODE_NOTIFICATION = 'Notification';
const URL_MODE_SUBSURVEY = 'Subsurvey';
const URL_SELECTION_KEY = 'selection';

export function CopyEditor() {
  const history = useHistory();
  const location = useLocation();
  const getUserInfo = usePost('/user/all_info');
  const [currentUser, setCurrentUser] = useState({} as Awaited<ReturnType<typeof getUserInfo>>);
  const config = useContext(ConfigurationContext);
  const interfaceContext = useContext(InterfaceContext);
  const languages: SupportedLanguage[] = (config['languages'] ?? 'en').split(',').map((s: string) => s.trim());
  const loggedInConfig = useContext(LoggedInConfigurationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const L = useLocalizedStrings();
  const [whatToEdit, setWhatToEdit] = useState<'Notification' | 'Subsurvey'>(modeFromParams());
  const [subsurveyPaths, setSubsurveyPaths] = useState<SubsurveyNode[] | null>(null);
  const [notifications, setNotifications] = useState<v0.Notification[] | null>(null);
  const [selectionName, setSelectionName] = useState<string | null>(selectionFromParams());
  const [excludedTypes, setExcludedTypes] = useState<Record<ExcludableType, boolean>>({ 'Show Field': true, 'Computed': true, 'Validated': true });
  const [editorLang, setEditorLang] = useState<SupportedLanguage>('en');
  const [previewLang, setPreviewLang] = useState<SupportedLanguage>('en');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [renderMarkdown, setRenderMarkdown] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  // This is needed because otherwise the closure of mergeEvents has an unfilled version of the userContext,
  // which results in an empty scope being used/returned when the survey needs to update.
  const thisUserRef = useRef<Awaited<ReturnType<typeof getUserInfo>>>();

  // Collab stuff
  const tabId = useRef(generateRandomString(4, 'abcdefghjkmnpqrstuvwxyz123456789'));
  const [author, setAuthor] = useState<Author>();
  const stateRef = useRef<State>();
  const [changeInProgress, setChangeInProgress] = useState(false);
  const addChange = useRef<(textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void>();

  // Publishing
  const [survey, setSurvey] = useState<v0.Root>({ survey: [], notifications: [], personas: [] });
  const loadSurvey = usePost('/survey/load_survey');
  const saveSurvey = usePost('/survey/save_survey', { compressRequestPayload: true });
  const [publishing, setPublishing] = useState(false);
  const [surveyValidationErrors, setSurveyValidationErrors] = useState<(string | { hint: string, error: string })[]>();
  const validateSurveyRS = useRoboScreener("/check_survey_validity", { compressRequestPayload: true });


  const { toast } = useToast();

  // State management & actions for Collab Mode
  const {
    collabEnabled, // if collab is not enable, this hook doesn't do much
    canPublish, // false if collab is actively receiving and merging events, otherwise true
    alertJustPublished, // tell listening clients that you just published
    getCollabState, // refresh collab events and settings
    // to be called whenever you make a distro change - this handles sending events in realtime
    // and also persisting new events to dynamo / s3
    handleNewCollabEvents,
    // uploads large amounts of events and saves a new version, with those events
    // merged into the exising ops.
    writeNewCollabVersion
  } = useCollabActions(
    'entireprogram',
    tabId.current,
    (events: ChangeSet, userActivity?: UserActivity[]) => {
      mergeEvents(events, userActivity);
    },
    true, // autosave
    // onSocketDisconnected
    () => {
      setCanEdit(false);
      setConnectionOpen(false);
    },
    // onSocketConnected
    () => {
      setConnectionOpen(true);
    }
  );

  useAsyncEffect(async () => {
    setCurrentUser(await getUserInfo({}));
  }, []);

  function modeFromParams() {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get(URL_MODE_KEY) === URL_MODE_NOTIFICATION
      ? URL_MODE_NOTIFICATION
      : URL_MODE_SUBSURVEY;
  }

  function selectionFromParams() {
    const urlParams = new URLSearchParams(location.search);
    return decodeURIComponent(urlParams.get(URL_SELECTION_KEY) ?? '');
  }

  // TODO: Should we log some metric on saves here? Probably. Where to?
  // Copied from Config.tsx - this should be updated/removed/extracted from this code.
  const publish = useCallback(async () => {
    if (!canPublish || !stateRef.current) return;
    const distroValidationResults = validateDistro(survey);
    if (distroValidationResults.some((result) => result.severity === 'error')) {
      alert('There are validation errors in the survey. Please contact support for help.');
      return;
    }

    const surveyStringForPublish = JSON.stringify(survey);

    setPublishing(true);
    try {
      const res = await validateSurveyRS({ survey: surveyStringForPublish });

      if (!res || 'error' in res) {
        setSurveyValidationErrors([res.error || 'An error occurred']);
        setPublishing(false);
        return;
      } else if (res.value.errors) {
        setSurveyValidationErrors(res.value.errors);
        setPublishing(false);
        return;
      }
    } catch (e) {
      console.log(e);
      setPublishing(false);
      return;
    }

    let surveyResp;
    try {
      surveyResp = await loadSurvey({ name: 'entireprogram', purpose: "checking survey has not changed" });
    } catch (e) {
      // It's only best effort;
    }

    let newUnpublishedEvents: ChangeSet = [];

    // Check for updates before saving to reduce clutter in versions page
    if (JSON.stringify(surveyResp?.config) !== surveyStringForPublish) {
      // after publishing, make sure the collab source of truth is aligned with what we just published.
      const toSend = JSON.stringify(getCompressedEvents(stateRef.current));
      const writeResponse = await writeNewCollabVersion({ toSend, publish: true });

      const collabResp = await getCollabState();
      newUnpublishedEvents = collabResp.unpublishedEvents;
      
      alertJustPublished();

      if (writeResponse.error !== '') {
        toast({
          description: writeResponse.error,
        })
      } else {
        toast({
          description: 'Successfully saved survey',
          variant: 'success'
        })
      }
    } else {
      toast({
        description: <span>Everything's up to date!<br /><br />No changes to save.</span>,
      })
    }

    stateRef.current.unpublishedChanges = newUnpublishedEvents;

    setPublishing(false);
  }, [survey, tabId, canPublish]);

  const mergeEvents = useCallback(async (events: ChangeSet, userActivity?: UserActivity[]) => {
    if (!events.length) {
      setChangeInProgress(false);
      return;
    }
    let latestRootTrackedObject: TrackedObject;
    if (!stateRef.current) {
      [latestRootTrackedObject, stateRef.current] = reconstructStateFromEvents(events);
    } else {
      // This function and updateUserActivity update the State that is passed into them, so we shouldn't need to reassign
      // stateRef.current here.
      latestRootTrackedObject = mergeStateFromEvents(stateRef.current, events)[0];
      if (userActivity) {
        updateUserActivity(stateRef.current, userActivity);
      }
    }

    // Note: If we called save_survey with this freshestSurvey object, that would be
    // equivalent to the "Real" save that happens upon publishing in Distro
    const freshestSurvey = latestRootTrackedObject.read(true);
    updateSubsurveyPaths(freshestSurvey);
    setSurvey(freshestSurvey);
    setChangeInProgress(false);
  }, []);

  function convertCopyEditsToCollabEvents(copyEdits: CopyEdits): ChangeSet {
    let events: ChangeSet = [];
    for (const id of Object.keys(copyEdits.edits || {})) {
      for (const lang of Object.keys((copyEdits.edits || {})[id] || {}) as SupportedLanguage[]) {
        // Create Collab "patch" operation
        const edit = copyEdits.edits[id][lang];
        if (edit) {
          const op: Patch = {
            op: 'patch',
            id: Math.random().toString(), // TODO: should we be using uuids here? Collab doesn't but that doesn't mean no.
            value: edit.value,
            prev_gen: Math.max(0, edit.prev_gen),
            author
          };
          events.push([id, 'primitive', op]);
        }
      }
    }
    events = removeDuplicateEvents(events);
    return events;
  }

  /**
   * Traverses an entire progrogram config and constructs (via mapSubsurveyPaths()) the paths
   * one could take while completing it, branching at conditionals, as a tree of renderable nodes
   */
  function updateSubsurveyPaths(survey: v0.Root) {
    // We do not use expandedSurvey.survey here because we want the unexpanded templates
    // so that we can correctly scope fields like q_ITER inside TemplatedBlocks.
    // NOTE: This may not function as expected if tags are assigned as part of iterations/substitutions
    // inside TemplatedBlocks themselves, but... that's whack anyway.
    const scope = collectScopeForTags(thisUserRef.current?.tags ?? [], (survey as v0.ExpandedSurvey).survey);
    const subSurveyPaths = mapSubsurveyPaths(survey, scope);
    setSubsurveyPaths(subSurveyPaths);
    const notificationsAndPayments = extractNotificationsAndPayments((survey as v0.ExpandedSurvey).survey, scope, true);
    setNotifications(notificationsAndPayments[0]);
  }

  const refreshCollabState = useCallback(async () => {
    const resp = await getCollabState();
    if (resp.collabEnabled) {
      const [rootTrackedObject, state] = reconstructStateFromEvents(resp.events);
      stateRef.current = state;
      stateRef.current.currentAuthor = { uid: currentUser.uid, tags: currentUser.tags, tabId: tabId.current }
      const surveyFromCollabEvents = rootTrackedObject.read(true);
      updateSubsurveyPaths(surveyFromCollabEvents);
      setCanEdit(true);
    } else {
      const surveyResp = await loadSurvey({ name: 'entireprogram', purpose: "load function called from copy editor" });
      updateSubsurveyPaths(surveyResp.config);
      setIsPreviewMode(true);
    }
  }, [getCollabState, currentUser, tabId]);

  // If connectionOpen has changed (and is now true), we need to re-initialize the collab state
  useEffect(() => {
    if (connectionOpen) {
      refreshCollabState();
    }
  }, [connectionOpen, refreshCollabState]);

  // General Initialization
  useEffect(() => {
    if (subsurveyPaths !== null || !currentUser.uid) {
      return;
    }

    thisUserRef.current = currentUser;

    if ((currentUser?.tags ?? []).some((tag) => ['copy_edit', 'admin'].includes(tag))) {
      if (!author) {
        setAuthor({ uid: currentUser.uid, tags: currentUser.tags, tabId: tabId.current });
      }
      setIsLoading(true);
      (async () => {
        await refreshCollabState();
        setIsLoading(false);
      })();
    }

  }, [subsurveyPaths, currentUser]);

  // Somewhat analogous to Config.tsx's handleUpdatesInner(), i.e. a function
  // that is called whenever there is an update, in this case to "edits"
  // This is how the various children update the collab state.
  // TODO: Could we move all of this down to the leaf nodes themselves so that the parent doesn't need
  // to manage so much state, and these functions don't need to get passed up/down the tree?
  useEffect(() => {
    addChange.current = (textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => {
      if (!trackedObjInfo[lang]) {
        // TODO: this may not be possible, but might be worth keeping just in case
        console.error('TrackedObject edit with language not in TrackedObject', trackedObjInfo, lang);
        return;
      }
      const { id, prev_gen } = trackedObjInfo[lang]!;

      if (collabEnabled) {
        const changesToEdits = {
          edits: {
            [id]: {
              [lang]: {
                value: textChange,
                prev_gen: prev_gen,
              }
            },
          },
          latestEditedObjectId: id
        } satisfies CopyEdits;

        // When we convert events to collab, they count as the same generation. We thus have to only convert them once,
        // so we only have the latest version of that generation (most recent edit)
        setChangeInProgress(true);
        if (stateRef.current && author && changesToEdits.latestEditedObjectId) {
          stateRef.current.myActivity = generateUserLocation(author, changesToEdits.latestEditedObjectId);
        }
        const copyEditsAsCollabChangeSet = convertCopyEditsToCollabEvents(changesToEdits);
        handleNewCollabEvents(copyEditsAsCollabChangeSet, stateRef.current?.myActivity, stateRef.current);
      }
      return;
    }
  }, [handleNewCollabEvents]);

  /**
   * Helper function which updates the URL params when either what type of thing, or which specific thing we
   * are editing changes. This will update the "location" hook, which will trigger a state update (and rerender)
   * via the useEffect below.
   * @param mode - what type of thing we are editing: 'Notification' or 'Subsurvey'
   * @param selection - name of the chosen thing to edit, for finding the actual object we're interested in
   */
  function updateSelection(mode: 'Notification' | 'Subsurvey', selection: string) {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set(URL_MODE_KEY, mode);
    urlParams.set(URL_SELECTION_KEY, encodeURIComponent(selection));
    history.push({
      pathname: location.pathname,
      search: urlParams.toString(),
    })
  }

  // Update internal state when URL changes. This will happen if a change is triggered by
  // a dropdown selection changing, or a user navigating with browser forward/back
  useEffect(() => {
    const modeParam = modeFromParams();
    if (modeParam !== whatToEdit) {
      setWhatToEdit(modeParam);
    }
    const selectionParam = selectionFromParams();
    if (selectionParam !== selectionName) {
      setSelectionName(selectionParam);
    }
  }, [location.search]); // Re-run when the query string changes

  // If we don't have a selection name when we get our list of subsurveys,
  // set the selection name to the first subsurvey's name and update the URL.
  // This should only run once, and only when navigating to the Copy Editor page without URL params.
  useEffect(() => {
    if (!selectionName && subsurveyPaths && subsurveyPaths.length > 0) {
      updateSelection(URL_MODE_SUBSURVEY, subsurveyPaths?.[0]?.name ?? '');
    }
  }, [subsurveyPaths]);

  // Find/set selected entity to edit if data or selection changes.
  // In both cases, if we fail to find the selected entity, we default to the first, if there is one
  const selectedToEdit = useMemo(() => {
    if (whatToEdit === 'Subsurvey') {
      return subsurveyPaths?.find((sp: SubsurveyNode) => sp.name === selectionName)?.subNodes ?? subsurveyPaths?.[0]?.subNodes ?? [];
    } else if (whatToEdit === 'Notification') {
      const selectedNotif = notifications?.find((notif: v0.Notification) => notif.name === selectionName) ?? notifications?.[0];
      return selectedNotif ? nodesFromNotif(selectedNotif) : [];
    }
  }, [subsurveyPaths, notifications, selectionName, whatToEdit]);

  // Rendering options split out into if/else to avoid nested ternaries
  if (isLoading || subsurveyPaths === null) {
    return (
      <div className='m-2'>
        <span className='text-[2rem] font-medium text-gray-900 py-3'>{'Copy Editor is loading... '}</span><SpacedSpinner className='text-sm p-2 mb-[2px] ml-2' />
      </div>
    );
  }

  // The editor, once loaded and confirmed collab is enabled
  return (
    <div className='w-max applicant-led'>
      {surveyValidationErrors &&
        <RoboscreenerSurveyErrorsModal
          errors={surveyValidationErrors}
          closeModal={() => setSurveyValidationErrors(undefined)}
        />
      }

      <div className='flex py-2'>
        <h3 className='mx-2 my-2'>{'Copy Editor'}</h3>
        {/* Dropdown for selecting survey vs notifications to edit */}
        <div className='ml-2 mx-3 min-w-[200px]'>
          <label htmlFor="what-to-edit-select" className="block mb-2 text-sm font-bold text-gray-900">Select what to edit</label>
          <select
            id="what-to-edit-select"
            value={whatToEdit}
            onChange={(e) => {
              // We use the first element of the subsurveys/notifs array as
              // a sensible default when switching between subsurveys and notifs
              let defaultSelection = '';
              if (e.target.value === 'Subsurvey') {
                defaultSelection = subsurveyPaths?.[0]?.name ?? '';
              } else if (e.target.value === 'Notification') {
                defaultSelection = notifications?.[0]?.name ?? '';
              }
              updateSelection(e.target.value as 'Notification' | 'Subsurvey', defaultSelection);
            }}
            className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
            aria-label="Select subsurvey to edit"
          >
            <option key={'Subsurvey'} value={'Subsurvey'}>{'Subsurvey'}</option>
            <option key={'Notification'} value={'Notification'}>{'Notification'}</option>
          </select>
        </div>
        {/* Dropdown for selecting Survey to edit */}
        <div className='ml-2 mx-3 min-w-[200px]'>
          <label htmlFor={`${whatToEdit}-select`} className="block mb-2 text-sm font-bold text-gray-900">{`Select ${whatToEdit}`}</label>
          <select
            id={`${whatToEdit}-select`}
            value={selectionName ?? ''}
            onChange={(e) => updateSelection(whatToEdit, e.target.value)}
            className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
            aria-label="Select subsurvey to edit"
          >
            {whatToEdit === 'Subsurvey' && subsurveyPaths.map((subsurvey, index) => (
              <option key={subsurvey.name + index} value={subsurvey.name}>
                {subsurvey.name}
              </option>
            ))}
            {whatToEdit === 'Notification' && (notifications || []).map((notif, index) => (
              <option key={notif.name + index} value={notif.name}>
                {notif.name}
              </option>
            ))}
          </select>
        </div>
        <div className='ml-2 mx-3 min-w-[200px] content-end'>
          <Button onClick={() => setLogoutModalOpen(true)} variant="secondary">
            {L.menu.logout}
          </Button>
        </div>

        <Modal title={L.menu.logout} open={logoutModalOpen} onClose={() => setLogoutModalOpen(false)}>
          <div>
            <p>Are you sure you want to logout?</p>
            <Button onClick={() => setLogoutModalOpen(false)} variant="primary">
              Cancel
            </Button>
            <Button onClick={() => window.location.href = '/logout'} variant="danger">
              {L.menu.logout}
            </Button>
          </div>
        </Modal>
      </div>

      {/* Header bar */}
      {/* z-index here to ensure dropdown stays on top */}
      <div className='z-10 sticky top-0 left-0 bg-white w-screen noprint'>
        <div className='flex py-2 shadow-md'>
          {/* Preview column language selector */}
          <div className='mx-3 w-[200px]'>
            <label htmlFor="language-select" className="block mb-2 text-sm font-bold text-gray-900">{'Select preview language'}</label>
            <select
              id="preview-language-select"
              value={previewLang}
              onChange={(e) => {
                setPreviewLang(e.target.value as SupportedLanguage);
                interfaceContext.setLanguage(e.target.value as SupportedLanguage);
              }}
              className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
              aria-label="Select preview language"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {supportedLanguages[lang]} {lang !== 'en' ? '(' + langToWord(lang, 'Name') + ')' : null}
                </option>
              ))}
            </select>
          </div>
          <div className='block mx-3 text-sm font-normal text-gray-700'>
            <div className='mb-1.5 font-bold'>Render Preview Markdown</div>
            <Switch label="Render Preview Markdown" hideLabel={true} checked={renderMarkdown} onCheckedChange={(checked) => setRenderMarkdown(checked)} />
          </div>
          {/* Editor column language selector */}
          <div className='mx-3 w-[200px]'>
            <label htmlFor="language-select" className="block mb-2 text-sm font-bold text-gray-900">{'Select editor language'}</label>
            <select
              id="editor-language-select"
              value={editorLang}
              onChange={(e) => {
                setEditorLang(e.target.value as SupportedLanguage);
              }}
              className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
              aria-label="Select editor language"
              disabled={!collabEnabled}
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {supportedLanguages[lang]} {lang !== 'en' ? '(' + langToWord(lang, 'Name') + ')' : null}
                </option>
              ))}
            </select>
          </div>
          {/* Toggles for showing/hiding certain Question Types */}
          <div className='mx-3 min-w-fit'>
            <fieldset className='grid grid-cols-2 grid-rows-2'>
              <legend className='text-sm font-bold text-gray-900'>
                Excluded Question Types
              </legend>
              {EXCLUDABLE_TYPES_ARRAY.map((t) =>
                <Checkbox
                  key={t}
                  checked={excludedTypes[t]}
                  onChange={(e) => {
                    setExcludedTypes(prevState => {
                      const newExcludedTypes = { ...prevState }
                      newExcludedTypes[t] = !newExcludedTypes[t];
                      return newExcludedTypes;
                    });
                  }}
                  label={t}
                />
              )}
            </fieldset>
          </div>
          {collabEnabled
            ? <>
              {whatToEdit === 'Subsurvey' && (
                <div className='block mx-3 text-sm font-normal text-gray-700'>
                  <div className='mb-1.5 font-bold'>Print mode</div>
                  <Switch label="Print mode" hideLabel={true} checked={isPreviewMode} onCheckedChange={(checked) => setIsPreviewMode(checked)} />
                </div>
              )}
              <div className='block mx-3 text-sm font-normal text-gray-700'>
                {changeInProgress ? 'Saving changes...' : 'Changes saved ✅'}
              </div>
            </>
            : <div className='text-red-500 block w-72'>Defaulting to Preview Mode as Collab is not enabled. To edit, enable Collab, or ask an Admin to do so.</div>
          }
          <div className='block mx-3 text-sm font-normal text-gray-700'>
            <div>Users here</div>
            <FacePile name={thisUserRef.current?.name || thisUserRef.current?.uid || ''} channel={get_deployment() + ':entireprogram'} browserTab={tabId.current} />
          </div>
          <div className='mx-3 block'>
            {loggedInConfig?.capabilities?.copyEditPublish
              ? <>
                <div className='text-sm font-bold text-gray-900'>Publish changes</div>
                <button aria-label="Publish Changes" onClick={publish} disabled={publishing || !canPublish || !collabEnabled}
                  className={'border-0 rounded-md mx-3 my-1.5 h-9 bg-blue-100 p-1.5 ' + ((canPublish && collabEnabled) ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-300' : 'text-gray-400')}>
                  {publishing
                    ? <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    : <div className="relative inline-block">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                  }
                </button>
              </>
              : <div className='text-sm text-gray-700'>
                Publishing disabled. Changes will be saved automatically, but will not become live until an admin publishes
              </div>
            }
          </div>
        </div>
      </div>

      {/* This is where the survey/editor is rendered */}
      {(selectionName && selectedToEdit) && (
        <div className='flex p-2 bg-gray-100'>
          <div className={`w-64 my-2 ${isPreviewMode ? 'ml-[200px]' : 'ml-2'} h-max p-2`} style={{ minWidth: '1600px' }}>
            {/* TODO: get this from the selection, not the name (for cases where URL param is borked) */}
            <h2>{selectionName}</h2>
            <Node
              key={selectionName + selectedToEdit?.length} // Always rerender if selectedSubsurvey changes
              nodes={selectedToEdit}
              excludedTypes={excludedTypes}
              addChange={addChange}
              previewLang={previewLang}
              editorLang={editorLang}
              isPreviewMode={isPreviewMode}
              canEdit={canEdit}
              renderMarkdown={renderMarkdown}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Transforms a Distro Notification object into a series of PathNodes that
 * the Copy Editor understands how to set up and display.
 */
function nodesFromNotif(notif: v0.Notification) {

  /**
   * Helper function to reduce the boilerplate of transforming the notif objects
   * for the Copy Editor
   */
  function createQuestionNode({ questionType, targetField, content, subContent, kind } : {
    questionType: QuestionNodeType['questionType'],
    targetField: QuestionNodeType['targetField'],
    content: v0.RichText | v0.ConditionalContentList,
    subContent?: QuestionNodeType['subContent'],
    kind?: QuestionNodeType['kind'],
  }): QuestionNodeType {
    const isConditionalContent = Array.isArray(content);
    if (isConditionalContent) {
      subContent = conditionalContentToPathNodes(content).concat(subContent ?? []);
    }

    return {
      type: 'Question',
      questionType,
      targetField,
      content: !isConditionalContent ? content : undefined,
      trackedObjInfo: !isConditionalContent ? (content as any)._trackedObjInfo : undefined,
      subContent: subContent,
      kind,
    };
  }

  // First we assemble the main notification and its subcontent
  const mainNotifSubContent: (QuestionNodeType | ConditionalNode)[] = [];
  mainNotifSubContent.push(createQuestionNode({
    questionType: 'SubContent',
    targetField: notif.targetPrefix + ' → Email Subject',
    content: notif.initial_notification.email_subject,
  }));
  if (notif.initial_notification.email_message) {
    mainNotifSubContent.push(createQuestionNode({
      questionType: 'SubContent',
      targetField: notif.targetPrefix + ' → Email Message',
      content: notif.initial_notification.email_message,
    }));
  }

  const mainNotifNode = createQuestionNode({
    questionType: notif.kind,
    targetField: notif.targetPrefix,
    content: notif.initial_notification.message,
    subContent: mainNotifSubContent,
    kind: notif.kind
  });

  const notifSubNodes: PathNode[] = [mainNotifNode];

  // Now we do essentially the same thing for each follow-up
  if (notif.kind === 'Notification' && notif.followups) {
    notif.followups.forEach((flup, index) => {
      const flupSubConent: (QuestionNodeType | ConditionalNode)[] = [];
      flupSubConent.push(createQuestionNode({
        questionType: 'SubContent',
        targetField: notif.targetPrefix + ' → ' + flup.suffix + ' → Email Subject',
        content: flup.email_subject
      }));
      if (flup.email_message) {
        flupSubConent.push(createQuestionNode({
          questionType: 'SubContent',
          targetField: notif.targetPrefix + ' → ' + flup.suffix + ' → Email Message',
          content: flup.email_message
        }));
      }

      // Purely for display purposes; a header in the UI for the follow-up
      const flupHeaderNode = {
        type: 'Section',
        name: { en: notif.name + ' - Follow-up # ' + (index + 1) },
      } satisfies SectionNode;

      const flupNode = createQuestionNode({
        questionType: notif.kind, // TODO: may want to invent something new for flups here
        targetField: notif.targetPrefix + ' → ' + flup.suffix,
        content: flup.message,
        subContent: flupSubConent,
      });

      const flupConditionNode: ConditionalNode = {
        type: 'Condition',
        condition: notif.initial_notification.enabled_when.kind === 'Click'
          ? CompileExpressionToEnglish(notif.initial_notification.enabled_when.expr)
          : notif.initial_notification.enabled_when.sql.substring(0, 100),
        subNodesTrue: [flupHeaderNode, flupNode]
      };

      notifSubNodes.push(flupConditionNode);
    });
  }

  // This ConditionalNode will contain the entire notification + flups
  const mainNotifConditionNode: ConditionalNode = {
    type: 'Condition',
    condition: notif.initial_notification.enabled_when.kind === 'Click'
      ? CompileExpressionToEnglish(notif.initial_notification.enabled_when.expr)
      : notif.initial_notification.enabled_when.sql.substring(0, 100),
    subNodesTrue: notifSubNodes
  };

  return [mainNotifConditionNode];
}
